import {
  type Club,
  type ClubSlot,
  type EventKind,
  type LessonItem,
  type SchoolEvent,
  type StudentGrade,
  addDays,
  canJoinLesson,
  clubsOnDay,
  gradeLevel,
  timeOf,
  todayIso,
  useBackpack,
  useClubMemberships,
  useClubs,
  useEvents,
  useLessons,
  weekStart,
} from '@shkola/api';
import { type ToneName, subjectLook, tones } from '@shkola/design';
import { Link } from '@tanstack/react-router';
import { type KeyboardEvent, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowUpRightIcon,
  BackpackIcon,
  BusIcon,
  CaretLeftIcon,
  CaretRightIcon,
  ConfettiIcon,
  type FilledIcon,
  ForkKnifeIcon,
  PuzzlePieceIcon,
  StarIcon,
  SunHorizonIcon,
  TrophyIcon,
  UsersThreeIcon,
  VideoCameraIcon,
} from '../../design/icons';
import { OnColorProvider, StatusPill, buttonClass, useAge } from '../../design/ui';
import { gradeShort } from '../../lib/labels';
import { useMinute } from '../../lib/now';
import { LessonCard, useLessonGrades } from './Day';
import {
  LEVEL_TONES,
  type Slot,
  type StudentContext,
  capitalize,
  dayLong,
  dayNow,
  isoWeekday,
  metaOf,
  minutesBetween,
  monthName,
  plain,
  slotsOf,
  useShownDay,
  weekdayShort,
} from './model';

/*
 * The lessons of «Скло» as a calendar (his words 26–27.10, refs «October 2023» for the days on top and
 * «Monday, June 3» for the rows): the days of the week to click, the day the home is about chosen; the
 * lessons as coloured rows with their times on the right; a break is only a narrow gap with its length;
 * «now» is a navy line across the day, a lesson going on fills with time. After the lessons — the
 * student's club; for the next day — what to pack. A click on a lesson opens its card.
 */

/** How an event looks everywhere on the home: a tone and a filled icon per kind. */
export const EVENT_LOOK: Record<EventKind, { tone: ToneName; icon: FilledIcon }> = {
  holiday: { tone: 'green', icon: SunHorizonIcon },
  celebration: { tone: 'pink', icon: ConfettiIcon },
  meeting: { tone: 'yellow', icon: UsersThreeIcon },
  trip: { tone: 'blue', icon: BusIcon },
  exam: { tone: 'violet', icon: TrophyIcon },
  other: { tone: 'teal', icon: StarIcon },
};

/** An event for the student: of the school or of their class, not for the staff or only the parents. */
export const forStudent = (ctx: StudentContext) => (e: SchoolEvent) =>
  e.audience !== 'staff' &&
  e.audience !== 'parents' &&
  e.audience !== 'class_parents' &&
  (e.classId === null || e.classId === ctx.child.class?.id);

/** The week on top and the day chosen in it: at first the day the home is about. */
export function useWeek(ctx: StudentContext) {
  const shown = useShownDay(ctx);
  const today = todayIso();
  const anchor = shown.date ?? today;
  const [picked, setPicked] = useState<string>();
  const selected = picked ?? anchor;
  const monday = weekStart(selected);
  const studentId = ctx.child.memberId;
  const scope = useMemo(() => ({ kind: 'student' as const, studentId }), [studentId]);
  const lessons = useLessons(ctx.schoolId, scope, monday, addDays(monday, 6));
  const events = useEvents(ctx.schoolId, monday);
  const all = lessons.data ?? [];
  const mine = (events.data ?? []).filter(forStudent(ctx));
  const on = (date: string) => mine.filter((e) => e.startsOn <= date && date <= e.endsOn);
  const weekend = [5, 6].filter((i) => all.some((l) => l.date === addDays(monday, i)));
  const days = [0, 1, 2, 3, 4, ...weekend].map((i) => {
    const date = addDays(monday, i);
    return { date, lessons: all.filter((l) => l.date === date), events: on(date) };
  });
  return {
    today,
    anchor,
    selected,
    pending: lessons.isPending || shown.pending,
    days,
    lessons: all.filter((l) => l.date === selected),
    events: on(selected),
    select: (date: string) => setPicked(date === anchor ? undefined : date),
    shift: (weeks: number) => setPicked(addDays(selected, weeks * 7)),
    back: () => setPicked(undefined),
  };
}

export type Week = ReturnType<typeof useWeek>;

/** The days as tiles: the weekday, the date, a red dot for a test and a dot of an event. */
function WeekDays({ week }: { week: Week }) {
  const { t } = useTranslation();
  return (
    <div
      className="grid gap-1.5"
      style={{ gridTemplateColumns: `repeat(${week.days.length}, minmax(0, 1fr))` }}
    >
      {week.days.map(({ date, lessons, events }) => {
        const on = date === week.selected;
        const isToday = date === week.today;
        const test = lessons.some((l) => l.isTest && l.status !== 'cancelled');
        const event = events.find((e) => e.kind !== 'holiday');
        const off = lessons.length === 0;
        return (
          <button
            key={date}
            type="button"
            aria-pressed={on}
            onClick={() => week.select(date)}
            title={isToday ? t('studentHome.day.today') : undefined}
            className={`relative flex h-[64px] min-w-0 flex-col items-center justify-center rounded-[18px] transition-colors ${
              on ? 'bg-primary' : 'bg-[#F3F4F8] hover:bg-[#EAECF2]'
            }`}
          >
            <span
              className={`text-[12px] leading-4 font-medium ${on ? 'text-white/70' : off ? 'text-faint' : 'text-muted'}`}
            >
              {weekdayShort(date)}
            </span>
            <span
              className={`font-display text-[22px] leading-7 font-semibold tracking-[-0.02em] ${on ? 'text-white' : off ? 'text-faint' : 'text-ink'}`}
            >
              {Number(date.slice(8))}
            </span>
            <span aria-hidden className="flex h-1.5 items-center gap-[3px]">
              {test && (
                <span
                  className="size-1.5 rounded-full"
                  style={{ backgroundColor: on ? '#FF9C97' : tones.red.vivid }}
                />
              )}
              {event && (
                <span
                  className="size-1.5 rounded-full"
                  style={{
                    backgroundColor: on ? '#FFFFFF' : tones[EVENT_LOOK[event.kind].tone].vivid,
                  }}
                />
              )}
            </span>
            {isToday && (
              <span
                aria-hidden
                className={`absolute top-2 right-2 size-1.5 rounded-full ${on ? 'bg-white' : 'bg-tone-blue-vivid'}`}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

const onKeys = (open: () => void) => (e: KeyboardEvent) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    open();
  }
};

/** The marks of a lesson, white on its colour: only what is not as usual. */
export function Marks({ lesson }: { lesson: LessonItem }) {
  const { t } = useTranslation();
  if (lesson.status === 'cancelled')
    return (
      <StatusPill tone="grey" size="xs">
        {t('studentHome.cancelled')}
      </StatusPill>
    );
  return (
    <>
      {lesson.isTest && (
        <StatusPill tone="red" size="xs">
          {t('studentHome.test')}
        </StatusPill>
      )}
      {lesson.substitute && (
        <StatusPill tone="orange" size="xs">
          {t('studentHome.substitute')}
        </StatusPill>
      )}
      {lesson.format === 'online' && (
        <StatusPill tone="violet" size="xs" icon={VideoCameraIcon}>
          {t('studentHome.online')}
        </StatusPill>
      )}
      {lesson.format === 'shelter' && (
        <StatusPill tone="yellow" size="xs">
          {t('studentHome.shelter')}
        </StatusPill>
      )}
    </>
  );
}

/** A lesson as a row in its subject's colour: the subject, the room and the teacher, the times. */
function LessonRow({
  lesson,
  grades,
  state,
  now,
  isToday,
  onOpen,
}: {
  lesson: LessonItem;
  grades: StudentGrade[];
  state: 'done' | 'live' | 'next' | 'ahead';
  now: number;
  isToday: boolean;
  onOpen: (lesson: LessonItem, el: HTMLElement) => void;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const tone = tones[subjectLook(lesson.subject).tone];
  const cancelled = lesson.status === 'cancelled';
  const start = Date.parse(lesson.startsAt);
  const end = Date.parse(lesson.endsAt);
  const join = isToday && state !== 'done' && canJoinLesson(lesson, todayIso()) && end > now;
  const open = () => ref.current && onOpen(lesson, ref.current);
  const ink = cancelled ? tones.grey.ink : tone.ink;
  return (
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={onKeys(open)}
      className={`relative flex w-full cursor-pointer items-center gap-3 overflow-hidden rounded-[14px] py-2 pr-3 pl-2.5 text-left transition-[filter,opacity] outline-none hover:brightness-[0.98] focus-visible:ring-2 focus-visible:ring-primary/40 ${cancelled ? 'opacity-50' : state === 'done' ? 'opacity-55' : ''}`}
      style={{
        backgroundColor: cancelled ? tones.grey.soft : tone.soft,
        boxShadow: state === 'live' ? `inset 0 0 0 1.5px ${tone.icon}` : undefined,
      }}
    >
      <span
        className="w-1 shrink-0 self-stretch rounded-full"
        style={{ backgroundColor: cancelled ? tones.grey.icon : tone.icon }}
      />
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-1.5">
          <span
            className={`truncate text-[14.5px] leading-5 font-semibold ${cancelled ? 'line-through' : ''}`}
            style={{ color: ink }}
          >
            {lesson.subject}
          </span>
          {/* a mark on a coloured row is white with coloured text (his rule) */}
          <span className="hidden shrink-0 items-center gap-1 sm:flex">
            <OnColorProvider value>
              <Marks lesson={lesson} />
            </OnColorProvider>
          </span>
        </span>
        <span
          className="mt-0.5 block truncate text-[12.5px] leading-[18px]"
          style={{ color: ink, opacity: 0.72 }}
        >
          {metaOf(t, lesson)}
        </span>
      </span>
      <OnColorProvider value>
        {grades.map((g) => (
          <StatusPill
            key={g.id}
            tone={
              g.kind === 'thematic' || g.kind === 'semester' || g.kind === 'year'
                ? 'violet'
                : g.value
                  ? (LEVEL_TONES[gradeLevel(g.value)] ?? 'grey')
                  : 'grey'
            }
            size="sm"
            className="min-w-7 justify-center"
          >
            {gradeShort(t, g.value, g.special)}
          </StatusPill>
        ))}
        {state === 'live' && (
          <StatusPill tone="green" dot size="xs">
            {t('studentHome.glass.week.left', {
              min: Math.max(1, Math.ceil((end - now) / 60_000)),
            })}
          </StatusPill>
        )}
        {state === 'next' && isToday && (
          <StatusPill tone="blue" size="xs">
            {t('studentHome.glass.week.in', { min: Math.max(1, minutesBetween(now, start)) })}
          </StatusPill>
        )}
      </OnColorProvider>
      {join && (
        <Link
          to="/lesson/$lessonId"
          params={{ lessonId: lesson.id }}
          onClick={(e) => e.stopPropagation()}
          className={buttonClass('primary', 'sm')}
        >
          {t('student.lessons.join')}
        </Link>
      )}
      <span className="flex shrink-0 flex-col items-end">
        <span className="text-[13.5px] leading-5 font-semibold" style={{ color: ink }}>
          {timeOf(lesson.startsAt)}
        </span>
        <span className="text-[12px] leading-[18px]" style={{ color: ink, opacity: 0.6 }}>
          {timeOf(lesson.endsAt)}
        </span>
      </span>
      {state === 'live' && (
        <span className="absolute inset-x-0 bottom-0 h-[3px] bg-white/70">
          <span
            className="block h-full"
            style={{
              width: `${Math.round(((now - start) / (end - start)) * 100)}%`,
              backgroundColor: tone.icon,
            }}
          />
        </span>
      )}
    </div>
  );
}

/** «Now» across the day: a navy knob and line, what it is now. */
function NowLine({ children }: { children: string }) {
  return (
    <div className="flex h-7 items-center gap-2 pl-0.5" aria-live="polite">
      <span className="size-2.5 shrink-0 rounded-full bg-primary shadow-[0_0_0_3px_#fff]" />
      <span className="shrink-0 text-[12.5px] font-semibold text-primary">{children}</span>
      <span className="h-[2px] flex-1 rounded-full bg-primary" />
    </div>
  );
}

/** A break that is not now: only a narrow gap with its length (a meal at the big one). */
export function Gap({ slot }: { slot: Extract<Slot, { kind: 'break' }> }) {
  const { t } = useTranslation();
  const big = slot.minutes >= 20;
  const label =
    slot.minutes >= 40
      ? t('studentHome.day.window', { min: slot.minutes })
      : big
        ? t('studentHome.day.bigBreak', { min: slot.minutes })
        : t('studentHome.day.break', { min: slot.minutes });
  return (
    <div className="flex h-5 items-center gap-1.5 pl-3 text-[11.5px] font-medium text-faint">
      {big && slot.minutes < 40 && <ForkKnifeIcon size={11} weight="fill" aria-hidden />}
      <span>{label}</span>
    </div>
  );
}

/** A club of the student after the lessons: a white row with a teal bar. */
export function ClubRow({ club, slot }: { club: Club; slot: ClubSlot }) {
  const { t } = useTranslation();
  return (
    <Link
      to="/clubs"
      className="flex items-center gap-3 rounded-[14px] bg-white py-2 pr-3 pl-2.5 ring-1 ring-[#E6E9F0] transition-colors ring-inset hover:bg-[#FAFBFC]"
    >
      <span
        className="w-1 shrink-0 self-stretch rounded-full"
        style={{ backgroundColor: tones.teal.icon }}
      />
      <PuzzlePieceIcon
        size={16}
        weight="fill"
        aria-hidden
        color={tones.teal.vivid}
        className="shrink-0"
      />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[14px] leading-5 font-semibold text-ink">
          {club.title}
        </span>
        <span className="block truncate text-[12.5px] leading-[18px] text-muted">
          {[t('studentHome.glass.week.club'), club.room].filter(Boolean).join(' · ')}
        </span>
      </span>
      <span className="flex shrink-0 flex-col items-end">
        <span className="text-[13.5px] leading-5 font-semibold text-ink">
          {slot.startsAt.slice(0, 5)}
        </span>
        <span className="text-[12px] leading-[18px] text-muted">{slot.endsAt.slice(0, 5)}</span>
      </span>
    </Link>
  );
}

/** An event of the day, above its lessons. */
export function EventRow({ event }: { event: SchoolEvent }) {
  const look = EVENT_LOOK[event.kind];
  const tint = tones[look.tone];
  const Icon = look.icon;
  return (
    <Link
      to="/events"
      className="flex h-10 items-center gap-2.5 rounded-[14px] px-3 transition-[filter] hover:brightness-[0.98]"
      style={{ backgroundColor: tint.soft }}
    >
      <Icon size={16} weight="fill" aria-hidden color={tint.vivid} className="shrink-0" />
      <span className="min-w-0 flex-1 truncate text-[13.5px] font-semibold text-ink">
        {event.title}
        {event.place && (
          <span className="font-normal" style={{ color: tint.ink }}>
            {' '}
            · {event.place}
          </span>
        )}
      </span>
      {event.atTime && (
        <span className="shrink-0 text-[13px] font-semibold" style={{ color: tint.ink }}>
          {event.atTime}
        </span>
      )}
    </Link>
  );
}

/** What to pack for the next school day, as one yellow row. */
export function PackRow({ ctx, date }: { ctx: StudentContext; date: string }) {
  const { t } = useTranslation();
  const pack = useBackpack(ctx.schoolId, ctx.child.memberId);
  if (pack.date !== date || pack.items.length === 0) return null;
  const tint = tones.yellow;
  return (
    <div
      className="mt-2 flex items-start gap-2.5 rounded-[14px] px-3 py-2.5"
      style={{ backgroundColor: tint.soft }}
    >
      <BackpackIcon
        size={16}
        weight="fill"
        aria-hidden
        color={tint.vivid}
        className="mt-0.5 shrink-0"
      />
      <p className="min-w-0 text-[13.5px] leading-5 text-ink">
        <span className="font-semibold">{t('studentHome.glass.week.pack')}:</span>{' '}
        {pack.items.map((i) => plain(i.label)).join(', ')}
      </p>
    </div>
  );
}

const roundButton =
  'inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-white text-ink ring-1 ring-stroke ring-inset transition-colors hover:bg-soft';

export function GlassDay({
  ctx,
  className = '',
  frame = 'rounded-[32px] bg-white p-4 shadow-[0_18px_40px_-28px_rgb(30_42_94/0.35)] md:p-6',
}: {
  ctx: StudentContext;
  className?: string;
  /** the block's own frame («Скло 2»: no shadow) */
  frame?: string;
}) {
  const { t } = useTranslation();
  const now = useMinute();
  const age = useAge();
  const week = useWeek(ctx);
  const clubs = useClubs(ctx.schoolId);
  const memberships = useClubMemberships([ctx.child.memberId]);
  const [open, setOpen] = useState<{ lesson: LessonItem; el: HTMLElement }>();
  const [unfolded, setUnfolded] = useState(false);
  const gradesOf = useLessonGrades(ctx, week.selected);
  const isToday = week.selected === week.today;
  const status = dayNow(week.lessons, now);
  const held = status.held;
  const slots = slotsOf(week.lessons);
  const holiday = week.events.find((e) => e.kind === 'holiday');
  const dayEvents = week.events.filter((e) => e.kind !== 'holiday');
  const dayClubs =
    held.length > 0
      ? clubsOnDay(
          clubs.data ?? [],
          memberships.data ?? [],
          ctx.child.memberId,
          isoWeekday(week.selected),
        )
      : [];
  const nextDay = week.anchor !== week.today && week.selected === week.anchor;

  // on a phone: from what goes on now three lessons on, the rest folded
  const focus = isToday
    ? Math.max(
        0,
        slots.findIndex(
          (s) =>
            (s.start <= now && now < s.end) || (s.kind === 'lesson' && s.lesson === status.next),
        ),
      )
    : 0;
  const lessonIndexes = slots
    .map((s, i) => (s.kind === 'lesson' ? i : -1))
    .filter((i) => i >= 0 && i >= focus);
  const lastShown = lessonIndexes[2] ?? slots.length - 1;
  const folded = (i: number) => !unfolded && (i < focus || i > lastShown);
  const foldedCount = slots.filter((s, i) => s.kind === 'lesson' && folded(i)).length;
  const anchorLabel =
    week.anchor === week.today
      ? t('studentHome.day.today')
      : week.anchor === addDays(week.today, 1)
        ? t('studentHome.day.tomorrow')
        : capitalize(weekdayShort(week.anchor));

  return (
    <section className={`flex min-w-0 flex-col ${frame} ${className}`}>
      <header className="mb-4 flex min-h-8 items-center gap-2">
        <h2
          className={`font-display font-semibold text-ink ${age === 'junior' ? 'text-[22px] leading-7' : 'text-[18px] leading-6'}`}
        >
          {t('studentHome.glass.week.title')}
        </h2>
        <span className="truncate text-[14px] text-muted">{monthName(week.selected)}</span>
        <span className="ml-auto flex shrink-0 items-center gap-1.5">
          {week.selected !== week.anchor && (
            <button
              type="button"
              onClick={week.back}
              className="inline-flex h-8 items-center rounded-full bg-primary px-3 text-[12.5px] font-semibold text-white transition-colors hover:bg-primary-hover"
            >
              {anchorLabel}
            </button>
          )}
          <button
            type="button"
            aria-label={t('studentHome.glass.week.prev')}
            onClick={() => week.shift(-1)}
            className={roundButton}
          >
            <CaretLeftIcon size={13} weight="bold" aria-hidden />
          </button>
          <button
            type="button"
            aria-label={t('studentHome.glass.week.next')}
            onClick={() => week.shift(1)}
            className={roundButton}
          >
            <CaretRightIcon size={13} weight="bold" aria-hidden />
          </button>
          <Link
            to="/schedule"
            aria-label={t('studentHome.glass.week.all')}
            title={t('studentHome.glass.week.all')}
            className={`${roundButton} duration-500 ease-spring hover:rotate-45`}
          >
            <ArrowUpRightIcon size={14} weight="bold" aria-hidden />
          </Link>
        </span>
      </header>

      <WeekDays week={week} />

      <p className="mt-3 mb-2 truncate text-[13px] text-muted">
        <span className="font-semibold text-ink">{dayLong(week.selected)}</span>
        {held[0] &&
          ` · ${t('studentHome.glass.week.summary', {
            lessons: t('student.lessonsCount', { count: held.length }),
            from: timeOf(held[0].startsAt),
            to: timeOf((held.at(-1) as LessonItem).endsAt),
          })}`}
      </p>

      {week.pending ? (
        <div className="flex flex-col gap-1.5" aria-busy>
          {[0, 1, 2, 3, 4].map((i) => (
            <span key={i} className="h-[54px] animate-pulse rounded-[14px] bg-soft" />
          ))}
        </div>
      ) : week.lessons.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 py-8 text-center">
          <span
            className="flex size-14 items-center justify-center rounded-full"
            style={{ backgroundColor: tones.green.soft, color: tones.green.vivid }}
          >
            <SunHorizonIcon size={26} weight="fill" aria-hidden />
          </span>
          <p className="text-[15px] font-semibold text-ink">
            {holiday?.title ?? t('studentHome.glass.week.weekend')}
          </p>
          <p className="text-[13px] text-muted">{t('studentHome.glass.week.none')}</p>
          {week.selected !== week.anchor && (
            <button
              type="button"
              onClick={week.back}
              className={`mt-2 ${buttonClass('outline', 'sm')}`}
            >
              {t('studentHome.glass.week.nextDay', { day: dayLong(week.anchor) })}
            </button>
          )}
        </div>
      ) : (
        <>
          {dayEvents.length > 0 && (
            <div className="mb-1.5 flex flex-col gap-1.5">
              {dayEvents.map((e) => (
                <EventRow key={e.id} event={e} />
              ))}
            </div>
          )}
          <ol className="flex flex-col">
            {isToday && status.before && status.first && (
              <li>
                <NowLine>
                  {t('studentHome.glass.week.nowBefore', {
                    min: minutesBetween(now, Date.parse(status.first.startsAt)),
                  })}
                </NowLine>
              </li>
            )}
            {slots.map((slot, index) => {
              const hidden = folded(index) ? 'max-md:hidden' : '';
              if (slot.kind === 'break') {
                const inside = isToday && slot.start <= now && now < slot.end;
                return (
                  <li key={`break-${slot.start}`} className={hidden}>
                    {inside ? (
                      <NowLine>
                        {t('studentHome.glass.week.nowBreak', {
                          min: Math.max(1, Math.ceil((slot.end - now) / 60_000)),
                        })}
                      </NowLine>
                    ) : (
                      <Gap slot={slot} />
                    )}
                  </li>
                );
              }
              const l = slot.lesson;
              const prev = slots[index - 1];
              const state =
                l.status === 'cancelled'
                  ? 'ahead'
                  : isToday && l === status.live
                    ? 'live'
                    : isToday && slot.end <= now
                      ? 'done'
                      : (isToday && l === status.next) || (!isToday && l === held[0] && nextDay)
                        ? 'next'
                        : 'ahead';
              return (
                // two lessons with no break between them: a hair of a gap
                <li key={l.id} className={`${prev?.kind === 'lesson' ? 'mt-1' : ''} ${hidden}`}>
                  <LessonRow
                    lesson={l}
                    grades={gradesOf(l)}
                    state={state}
                    now={now}
                    isToday={isToday}
                    onOpen={(lesson, el) => setOpen({ lesson, el })}
                  />
                </li>
              );
            })}
            {isToday && status.over && status.last && (
              <li className="mt-1">
                <NowLine>
                  {t('studentHome.glass.week.nowOver', { time: timeOf(status.last.endsAt) })}
                </NowLine>
              </li>
            )}
          </ol>
          {foldedCount > 0 && (
            <button
              type="button"
              onClick={() => setUnfolded(true)}
              className="mt-2 self-start rounded-full bg-soft px-3.5 py-1.5 text-[13px] font-semibold text-ink-soft hover:text-ink md:hidden"
            >
              {t('studentHome.day.more', { count: foldedCount })}
            </button>
          )}
          {dayClubs.length > 0 && (
            <div className="mt-2 flex flex-col gap-1.5">
              {dayClubs.map(({ club, slot }) => (
                <ClubRow key={`${club.id}-${slot.startsAt}`} club={club} slot={slot} />
              ))}
            </div>
          )}
          {nextDay && <PackRow ctx={ctx} date={week.selected} />}
        </>
      )}
      {open && (
        <LessonCard
          ctx={ctx}
          lesson={open.lesson}
          anchor={open.el}
          onClose={() => setOpen(undefined)}
        />
      )}
    </section>
  );
}
